/**
 * Import Service
 *
 * Provides methods for bulk CSV import of Users, Companies, Locations, and
 * Contacts. Each import method sends a multipart/form-data POST request containing
 * the CSV file. The backend parses, validates, and creates records, then
 * returns a summary of the import operation.
 *
 * Also provides a client-side template generator that produces downloadable
 * CSV files with the correct column headers for each entity type.
 *
 * Backend endpoints:
 *	 POST /api/v1/import/users		— Bulk-import users
 *	 POST /api/v1/import/companies	— Bulk-import companies
 *	 POST /api/v1/import/locations	— Bulk-import locations
 *	 POST /api/v1/import/contacts	— Bulk-import contacts
 *
 * All endpoints require authentication (Sanctum Bearer token) and
 * admin/super_admin/developer role authorization.
 */

/** Base URL for all API requests; mirrors the api.ts configuration */
const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

/**
 * Represents a single error that occurred during import processing.
 *
 * @property row - The 1-based line number in the CSV where the error occurred
 * @property message - Human-readable description of what went wrong
 */
export interface ImportError {
	row: number;
	message: string;
}

/**
 * Represents a row that was skipped because a duplicate already exists.
 *
 * @property row - The 1-based line number in the CSV of the duplicate row
 * @property email - The duplicate email address (for user and contact imports)
 * @property name - The duplicate entity name (for company/location/contact imports)
 * @property address - The duplicate address (for location imports)
 */
export interface ImportDuplicate {
	row: number;
	email?: string;
	name?: string;
	address?: string;
}

/**
 * Summary result returned by all import endpoints.
 *
 * @property imported - Count of successfully created records
 * @property skipped - Count of rows skipped due to duplicates
 * @property errors - Array of row-level validation/creation errors
 * @property duplicates - Array of rows skipped because duplicates already exist
 */
export interface ImportResult {
	imported: number;
	skipped: number;
	errors: ImportError[];
	duplicates: ImportDuplicate[];
}

/**
 * Sends a CSV file to the specified import endpoint via multipart/form-data.
 *
 * Uses fetch() directly (instead of the api.ts client) because the api.ts
 * client always sets Content-Type to application/json and JSON-stringifies
 * the body. Multipart file uploads require the browser to auto-set the
 * Content-Type header with the correct boundary string.
 *
 * @param endpoint - The API path relative to BASE_URL (e.g., "/import/users")
 * @param file - The CSV File object selected by the user
 * @returns The parsed ImportResult from the backend
 * @throws Error if the request fails or the backend returns a non-2xx status
 */
async function uploadCsv(endpoint: string, file: File): Promise<ImportResult> {
	// Build a FormData payload with the CSV file under the 'file' key
	const formData = new FormData();
	formData.append('file', file);

	// Retrieve the auth token for the Authorization header
	const token = localStorage.getItem('auth_token');

	// Send the multipart request — do NOT set Content-Type manually;
	// the browser will auto-set it with the correct boundary string
	const response = await fetch(`${BASE_URL}${endpoint}`, {
		method: 'POST',
		headers: {
			...(token ? { Authorization: `Bearer ${token}` } : {}),
			// Explicitly set Accept to ensure JSON response
			Accept: 'application/json',
		},
		body: formData,
	});

	// Parse the JSON response body
	const data = await response.json();

	// Throw an error for non-2xx responses so callers can handle failures
	if (!response.ok) {
		throw new Error(data.message || 'Import failed. Please try again.');
	}

	return data as ImportResult;
}

/**
 * The entity types that can be bulk-imported from a CSV file.
 * One per ImportController endpoint.
 */
export type ImportEntityType = 'users' | 'companies' | 'locations' | 'contacts';

/**
 * CSV column headers for each entity type's import template.
 * These match the expected columns documented in ImportController.
 *
 * Exported because the CsvImport organism renders the same list as its
 * "expected vs detected" column mapping — keeping one copy means the
 * downloadable template and the mapping check can never drift apart.
 */
export const IMPORT_TEMPLATE_HEADERS: Record<ImportEntityType, string[]> = {
	users: ['first_name', 'last_name', 'email', 'phone', 'role', 'company_name'],
	companies: ['name', 'address', 'address_line_2', 'city', 'state', 'zip', 'phone', 'billing_email', 'tax_rate'],
	locations: ['name', 'address', 'address_line_2', 'city', 'state', 'zip', 'cleanable_sqft', 'business_line', 'company_name'],
	contacts: ['first_name', 'last_name', 'email', 'phone', 'title', 'notes', 'company_name'],
};

/**
 * Import service methods for CSV bulk operations.
 */
export const importService = {
	/**
	 * POST /api/v1/import/users — Upload a CSV to bulk-import user records.
	 *
	 * @param file - The CSV file with user data (first_name, last_name, email, etc.)
	 * @returns Import summary with counts and details of imported/skipped/errored rows
	 */
	importUsers: (file: File): Promise<ImportResult> =>
		uploadCsv('/import/users', file),

	/**
	 * POST /api/v1/import/companies — Upload a CSV to bulk-import company records.
	 *
	 * @param file - The CSV file with company data (name, address, city, etc.)
	 * @returns Import summary with counts and details
	 */
	importCompanies: (file: File): Promise<ImportResult> =>
		uploadCsv('/import/companies', file),

	/**
	 * POST /api/v1/import/locations — Upload a CSV to bulk-import location records.
	 *
	 * @param file - The CSV file with location data (name, address, business_line, etc.)
	 * @returns Import summary with counts and details
	 */
	importLocations: (file: File): Promise<ImportResult> =>
		uploadCsv('/import/locations', file),

	/**
	 * POST /api/v1/import/contacts — Upload a CSV to bulk-import contact records.
	 *
	 * Contacts are customer-side people (site managers, superintendents), not
	 * platform users — no login is created. Every row must resolve to a company
	 * via company_name or company_id, since contacts.company_id is NOT NULL.
	 *
	 * @param file - The CSV file with contact data (first_name, last_name, company_name, etc.)
	 * @returns Import summary with counts and details
	 */
	importContacts: (file: File): Promise<ImportResult> =>
		uploadCsv('/import/contacts', file),

	/**
	 * Generate and download a CSV template file with the correct headers
	 * for the specified entity type. Creates a Blob URL and triggers a
	 * browser download using a temporary anchor element.
	 *
	 * @param type - The entity type: 'users', 'companies', 'locations', or 'contacts'
	 */
	downloadTemplate: (type: ImportEntityType): void => {
		// Get the header row for the requested entity type
		const headers = IMPORT_TEMPLATE_HEADERS[type];

		// Build the CSV content — just the header row with a trailing newline
		const csvContent = headers.join(',') + '\n';

		// Create a Blob with the CSV content and trigger a download
		const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
		const url = URL.createObjectURL(blob);

		// Create a temporary anchor element to trigger the browser download
		const link = document.createElement('a');
		link.href = url;
		link.download = `${type}_import_template.csv`;
		document.body.appendChild(link);
		link.click();

		// Clean up the temporary DOM element and object URL
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	},
};
